import { ConfigProvider, Drawer, Empty, Input, Select, Spin } from 'antd';
import styled from 'styled-components';
import { Button, Colors, Row, Text } from 'basic-ui-lib';
import { RadioButtons } from 'basic-ui-lib/components/RadioButtons';
import { useEffect, useMemo, useState } from 'react';
import useGetListStores from 'modules/store/services/hooks/useGetListStore';
import { IApiGetPlaylistParam } from 'modules/playlist/interfaces/api';

interface PlaylistPickerModalProps {
  open?: boolean;
  value?: string;
  onChange?: (storeId: string) => void;
  playlistOptions?: { label: string; value: string }[];
  onClose?: () => void;
  currentStoreId?: string;
  fetchPlaylists: (params: IApiGetPlaylistParam) => void;
  loading: boolean;
  setPlaylistName: (name: string) => void;
}

const PlaylistPickerModal = ({
  value,
  onChange,
  playlistOptions,
  open,
  onClose,
  currentStoreId,
  fetchPlaylists,
  loading,
  setPlaylistName,
}: PlaylistPickerModalProps) => {
  const [playlistId, setPlaylistId] = useState(value || '');
  const [selectedStoreId, setSelectedStoreId] = useState(currentStoreId || '');
  const [search, setSearch] = useState('');

  const handleChoose = () => {
    onChange?.(playlistId);
    onClose?.();
  };

  useEffect(() => {
    if (currentStoreId && open) {
      setSelectedStoreId(currentStoreId);
      fetchPlaylists({ page: 1, size: 100, storeId: currentStoreId });
    }
  }, [open]);

  const { storesList: storesData } = useGetListStores(true);

  const storeOptions = useMemo(() => {
    return (storesData?.content ?? []).map((store) => ({
      label: store.storeName,
      value: store.storeId,
    }));
  }, [storesData]);

  const handleStoreChange = (storeId: string) => {
    setSelectedStoreId(storeId);
    setSearch('');
    fetchPlaylists({ page: 1, size: 100, storeId });
  };

  const filteredPlaylistOptions = useMemo(() => {
    if (!search) return playlistOptions || [];
    return (playlistOptions || []).filter((playlist) =>
      playlist.label.toLowerCase().includes(search.toLowerCase()),
    );
  }, [playlistOptions, search]);

  return (
    <ConfigProvider>
      <StyledDrawer
        title={
          <Row flex={1} justifyContent='center' alignItems='center'>
            <Text title_h6 semibold center>
              Playlist
            </Text>
          </Row>
        }
        placement='bottom'
        closable={false}
        onClose={onClose}
        open={open}
        height='85%'
        footer={
          <Row>
            <Button type_cancel lg onClick={onClose} style={{ width: '100%' }}>
              Close
            </Button>
            <Button
              type_second
              lg
              style={{ width: '100%' }}
              onClick={handleChoose}
            >
              OK
            </Button>
          </Row>
        }
        destroyOnClose
        style={{ borderRadius: '10px 10px 0px 0px' }}
      >
        <Select
          showSearch
          optionFilterProp='label'
          value={selectedStoreId || undefined}
          options={storeOptions}
          placeholder='Select a store'
          onChange={handleStoreChange}
          filterOption={(input, option) =>
            String(option?.label ?? '')
              .toLowerCase()
              .includes(input.toLowerCase())
          }
          style={{ width: '100%' }}
          size='large'
          popupMatchSelectWidth
        />

        <CustomSearch
          allowClear
          placeholder='Search playlist...'
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          size='large'
        />
        <Text body_1 regular color='neutral.text.6' left>
          Playlists ({loading ? '...' : filteredPlaylistOptions?.length})
        </Text>
        {loading ? (
          <LoadingContainer>
            <Spin />
            <Text body_1 regular color='neutral.text.6'>
              Loading playlists...
            </Text>
          </LoadingContainer>
        ) : !selectedStoreId ? (
          <Empty description='Please select a store' />
        ) : filteredPlaylistOptions?.length === 0 ? (
          <Empty
            description={
              search
                ? 'No playlists match your search'
                : 'This store has no playlists yet'
            }
          />
        ) : (
          <RadioButtons
            options={filteredPlaylistOptions}
            value={playlistId}
            onChange={(value) => {
              setPlaylistId(value);
              const selectedPlaylist = filteredPlaylistOptions.find(
                (playlist) => playlist.value === value,
              );
              if (selectedPlaylist) {
                setPlaylistName(selectedPlaylist.label);
              }
            }}
            className='radio-styled'
          />
        )}
      </StyledDrawer>
    </ConfigProvider>
  );
};

PlaylistPickerModal.displayName = 'PlaylistPickerModal';
export default PlaylistPickerModal;

const StyledDrawer = styled(Drawer)`
  .ant-drawer-body {
    padding: 16px 16px 32px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    background-color: ${Colors.neutral.bg[2]};
  }

  .ant-drawer-footer {
    padding: 16px 16px;
    box-shadow: 0px -4px 8px 0px #0000001a;
    background-color: ${Colors.neutral.bg[1]};
  }

  .radio-styled {
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    row-gap: 16px;
    .custom-text {
      font-size: 16px;
    }
  }
`;

const CustomSearch = styled(Input.Search)`
  .ant-input-affix-wrapper {
    padding: 0 12px;
  }

  .ant-input-search-button .anticon {
    font-size: 18px;
  }

  .ant-input-suffix {
    .anticon {
      font-size: 18px;
    }
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex: 1;
  min-height: 180px;
`;
